import {Injectable} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {AndroidSmsRetriever} from '@capgo/capacitor-android-sms-retriever';
import {environment} from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SmsAutofillService {
  private isListening = false;

  constructor() {}

  /**
   * Check if SMS Retriever API is available
   */
  isAvailable(): boolean {
    return (
      Capacitor.isPluginAvailable('AndroidSmsRetriever') &&
      Capacitor.getPlatform() === 'android'
    );
  }

  /**
   * Get app signature hash for backend
   * Should be called once during development to share with backend team
   * @returns Promise resolving to app hash string (e.g., "FA+9qCX9VSu")
   */
  async getAppHash(): Promise<string> {
    if(!this.isAvailable()) {
      throw new Error('SMS Retriever API not available on this platform');
    }

    try {
      const result = await AndroidSmsRetriever.getHashString();
      return result.hash;
    } catch (error) {
      console.error('[SmsAutofillService] Failed to get app hash:', error);
      throw error;
    }
  }

  /**
   * Start listening for SMS and extract OTP code
   * Returns Promise that resolves with 6-digit OTP code
   * Auto-times out after 5 minutes (API limitation)
   * In dev mode without the plugin, shows a mock OTP dialog
   * @returns Promise resolving to 6-digit OTP string or null if failed
   */
  async startWatching(): Promise<string | null> {
    if(!this.isAvailable()) {
      // Dev mode: simulate SMS receipt
      if(!environment.production) {
        console.debug('[SmsAutofillService] Dev mode: SMS Retriever not available, using mock');
        return this.mockSmsWatcher();
      }
      
      console.debug('[SmsAutofillService] SMS Retriever not available, skipping');
      return null; // Graceful fallback to manual entry
    }

    if(this.isListening) {
      console.debug('[SmsAutofillService] Already listening, stopping previous watcher');
      await this.stopWatching();
    }

    return new Promise((resolve) => {
      try {
        this.isListening = true;
        console.debug('[SmsAutofillService] Starting SMS listener...');

        // Listen for SMS received
        const smsSubscription = AndroidSmsRetriever.addListener('smsReceived',
          ({message}) => {
            console.debug('[SmsAutofillService] SMS received:', message);

            // Extract 6-digit OTP from SMS message
            // Matches patterns like: "123456", "code: 123456", "is: 123456", etc.
            const otpMatch = message.match(/\b\d{6}\b/);

            if(otpMatch) {
              const otp = otpMatch[0];
              console.debug('[SmsAutofillService] OTP extracted:', otp);
              smsSubscription.then((subscription) => {
                subscription.remove();
              });
              timeoutSubscription.then((subscription) => {
                subscription.remove();
              });
              this.isListening = false;
              resolve(otp);
            } else {
              console.warn('[SmsAutofillService] No 6-digit OTP found in SMS');
            }
          });

        // Listen for timeout
        const timeoutSubscription = AndroidSmsRetriever.addListener('smsRetrieverTimeout',
          () => {
            console.debug('[SmsAutofillService] SMS Retriever timed out');
            smsSubscription.then((subscription) => {
              subscription.remove();
            });
            timeoutSubscription.then((subscription) => {
              subscription.remove();
            });
            this.isListening = false;
            resolve(null);
          });

        // Start watching
        AndroidSmsRetriever.startWatch();
      } catch (error) {
        console.error('[SmsAutofillService] SMS watcher error:', error);
        this.isListening = false;
        resolve(null);
      }
    });
  }

  /**
   * Mock SMS watcher for dev environment without plugin
   * Prompts user to enter OTP manually
   */
  private mockSmsWatcher(): Promise<string | null> {
    return new Promise((resolve) => {
      const otp = prompt('[DEV MODE] Enter 6-digit OTP to simulate SMS receipt:',
        '123456');
      
      if(otp && /^\d{6}$/.test(otp)) {
        console.debug('[SmsAutofillService] Mock OTP entered:', otp);
        resolve(otp);
      } else {
        console.warn('[SmsAutofillService] Mock OTP cancelled or invalid');
        resolve(null);
      }
    });
  }

  /**
   * Stop listening for SMS
   */
  async stopWatching(): Promise<void> {
    if(!this.isAvailable()) {
      return;
    }

    try {
      await AndroidSmsRetriever.stopWatch();
      this.isListening = false;
      console.debug('[SmsAutofillService] SMS listener stopped');
    } catch (error) {
      console.error('[SmsAutofillService] Error stopping SMS listener:', error);
    }
  }

  /**
   * Check if currently listening for SMS
   */
  isCurrentlyListening(): boolean {
    return this.isListening;
  }
}
